import { useRef } from 'react';
import axios from 'axios';
import { Editor } from '@tiptap/react';
import { endpoints, baseUrl } from '../../../api/api';
import { Section } from '../types';
import { useAlert } from '../../../components/AlertBanner';
import { trackImageInserted, trackEditorImageUploadFailed, trackImageUrlConversionFailed } from '../../../utils/analytics';

const MAX_IMAGE_WIDTH = 1800;
const JPEG_QUALITY = 0.8;
const MAX_PASTE_IMAGE_BYTES = 8 * 1024 * 1024; // 8 MB hard block after compression

// Compress a data URL to max 1800px wide at JPEG 80% quality using canvas.
// Returns the compressed data URL, or the original if compression fails.
export const compressBase64Image = (dataUrl: string): Promise<string> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = img.naturalWidth > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH / img.naturalWidth : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(dataUrl); return; }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

// Standalone (non-hook) version: compress all base64 images in an HTML string,
// upload each to the server, and return the HTML with CDN URLs in place of base64.
// onProgress is called after each image finishes uploading.
export const uploadImagesInHtml = async (
  html: string,
  bookId: string | null,
  onProgress?: (uploaded: number, total: number) => void,
): Promise<string> => {
  const regex = /src="(data:image\/[^;]+;base64,[^"]+)"/g;
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = regex.exec(html)) !== null) seen.add(m[1]);
  if (seen.size === 0) return html;

  const total = seen.size;
  let uploaded = 0;

  const entries = await Promise.all(
    Array.from(seen).map(async (original): Promise<[string, string]> => {
      try {
        const compressed = await compressBase64Image(original);
        try {
          const res = await axios.post(endpoints.tiptapImageCallback, { image: compressed, bookId });
          if (res.data.success && res.data.url) {
            onProgress?.(++uploaded, total);
            return [original, res.data.url];
          }
        } catch { /* upload failed — keep compressed base64 */ }
        onProgress?.(++uploaded, total);
        return [original, compressed];
      } catch {
        onProgress?.(++uploaded, total);
        return [original, original];
      }
    }),
  );

  const replacements = new Map(entries);
  return html.replace(/src="(data:image\/[^;]+;base64,[^"]+)"/g, (fullMatch, dataUrl) => {
    const r = replacements.get(dataUrl);
    return r !== undefined ? `src="${r}"` : fullMatch;
  });
};

export const useImageUpload = (bookId: string | null, editor: Editor | null) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showAlert } = useAlert();

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showAlert('Please select an image file', 'warning');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showAlert('Image size must be less than 5MB', 'warning');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', 'other');
      if (bookId) {
        formData.append('bookId', bookId);
      }

      const response = await axios.post(endpoints.uploadFile, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileUrl = response.data.url || `${baseUrl}/api/files/public/${response.data.file?.filename}`;

      if (editor) {
        editor.chain().focus().setImage({ src: fileUrl }).run();
        trackImageInserted();
      }
    } catch (error: any) {
      console.error('Failed to upload image:', error);
      trackEditorImageUploadFailed(error.response?.data?.message || error.message || 'unknown');
      showAlert(error.response?.data?.message || 'Failed to upload image. Please try again.', 'error');
    }

    event.target.value = '';
  };

  // Helper function to upload base64 images and replace with URLs
  const uploadBase64Images = async (htmlContent: string): Promise<string> => {
    const base64Regex = /src="(data:image\/[^;]+;base64,[^"]+)"/g;
    const matches: string[] = [];
    let match: RegExpExecArray | null;

    while ((match = base64Regex.exec(htmlContent)) !== null) {
      matches.push(match[1]);
    }

    if (matches.length === 0) {
      return htmlContent;
    }

    let updatedContent = htmlContent;

    for (const base64Data of matches) {
      try {
        const response = await axios.post(endpoints.tiptapImageCallback, {
          image: base64Data,
          bookId: bookId
        });

        if (response.data.success && response.data.url) {
          updatedContent = updatedContent.replace(base64Data, response.data.url);
        }
      } catch (error: any) {
        console.error('Failed to upload base64 image:', error);
        trackImageUrlConversionFailed(error?.response?.data?.message || error?.message || 'unknown');
      }
    }

    return updatedContent;
  };

  // Helper function to process all sections and upload their base64 images
  const processAndUploadImages = async (sectionsToProcess: Section[]): Promise<Section[]> => {
    const processedSections: Section[] = [];

    for (const section of sectionsToProcess) {
      const processedContent = await uploadBase64Images(section.content);
      processedSections.push({
        ...section,
        content: processedContent
      });
    }

    return processedSections;
  };

  // Process pasted HTML: compress all embedded base64 images in parallel, then upload.
  // Designed to be called from the editor's handlePaste event before content enters the doc.
  const handlePasteImages = async (html: string): Promise<string> => {
    const regex = /src="(data:image\/[^;]+;base64,[^"]+)"/g;
    const seen = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = regex.exec(html)) !== null) seen.add(m[1]);
    if (seen.size === 0) return html;

    // Compress + upload every unique image in parallel
    const entries = await Promise.all(Array.from(seen).map(async (original): Promise<[string, string]> => {
      try {
        const compressed = await compressBase64Image(original);
        const sizeBytes = Math.round((compressed.length * 3) / 4);
        if (sizeBytes > MAX_PASTE_IMAGE_BYTES) {
          // Image still too large after compression — remove it
          return [original, ''];
        }
        try {
          const res = await axios.post(endpoints.tiptapImageCallback, { image: compressed, bookId });
          if (res.data.success && res.data.url) return [original, res.data.url];
        } catch { /* upload failed — fall through to compressed base64 */ }
        return [original, compressed];
      } catch {
        return [original, original]; // compression failed — keep original
      }
    }));

    const replacements = new Map(entries);
    const removed = entries.filter(([, v]) => v === '').length;
    if (removed > 0) {
      showAlert(`${removed} image${removed > 1 ? 's were' : ' was'} too large (>8 MB) and removed.`, 'warning');
    }

    // Single-pass replacement — avoids repeated scans of a large HTML string
    return html.replace(/src="(data:image\/[^;]+;base64,[^"]+)"/g, (fullMatch, dataUrl) => {
      const r = replacements.get(dataUrl);
      if (r === undefined) return fullMatch;
      if (r === '') return 'src=""'; // removed image — broken-image placeholder
      return `src="${r}"`;
    });
  };

  return {
    fileInputRef,
    handleImageUpload,
    handleFileChange,
    processAndUploadImages,
    handlePasteImages,
  };
};
